import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetOrganizationContactsDocument,
  CreateOrganizationContactDocument,
  type GetOrganizationContactsQuery,
  type OrganizationContactFilterInput,
  type CreateOrganizationContactMutation,
  type CreateOrganizationContactMutationVariables,
} from '@/generated/root-types';
import { DataTable } from '@gastrosoft/shared/ui';
import { setOverrideTenantId } from '@gastrosoft/shared/utils';
import { useNotification } from '@/app/contexts/NotificationContext';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';

interface Row extends Record<string, unknown> {
  id: string;
  displayName: string;
  email: string;
  orgName: string;
  salutation: string | null;
  position: string | null;
  tenantName: string;
  updatedAt: string;
}

export default function OrganizationContacts() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get('tenantId') ?? undefined;
  const organizationId = searchParams.get('organizationId') ?? undefined;
  const { showSuccess, showError } = useNotification();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [position, setPosition] = useState('');
  const [salutation, setSalutation] = useState('');

  useEffect(() => {
    if (tenantId) setOverrideTenantId(tenantId);
    else setOverrideTenantId(null);
    return () => setOverrideTenantId(null);
  }, [tenantId]);

  const where: OrganizationContactFilterInput = useMemo(() => {
    if (organizationId) return { organizationId: { eq: organizationId } };
    if (tenantId) return { tenantId: { eq: tenantId } };
    return {};
  }, [organizationId, tenantId]);

  const { data, loading, refetch } = useQuery<GetOrganizationContactsQuery>(
    GetOrganizationContactsDocument,
    {
      variables: { first: GRAPHQL_MAX_PAGE_SIZE, where },
    },
  );

  const [createContact, { loading: creating }] = useMutation<
    CreateOrganizationContactMutation,
    CreateOrganizationContactMutationVariables
  >(CreateOrganizationContactDocument);

  const nodes = data?.organizationContacts?.nodes ?? [];
  const rows: Row[] = nodes.map((n) => ({
    id: n.id,
    displayName: [n.firstName, n.lastName].filter(Boolean).join(' ') || n.email || '–',
    email: n.email ?? '–',
    orgName: n.organization?.name ?? '–',
    salutation: n.salutation ?? null,
    position: n.position ?? null,
    tenantName: n.tenant?.name ?? '–',
    updatedAt: n.updatedAt,
  }));

  const columns: GridColDef[] = [
    {
      field: 'salutation',
      headerName: t('orgContacts.salutation'),
      width: 100,
      valueFormatter: (v: string | null) => v || '–',
    },
    { field: 'displayName', headerName: t('common.name'), flex: 1, minWidth: 140 },
    { field: 'email', headerName: t('organizationDetail.email'), flex: 1, minWidth: 160 },
    {
      field: 'position',
      headerName: t('orgContacts.position'),
      width: 130,
      valueFormatter: (v: string | null) => v || '–',
    },
    { field: 'orgName', headerName: t('organizationDetail.customer'), flex: 1, minWidth: 130 },
    { field: 'tenantName', headerName: t('common.tenant'), flex: 1, minWidth: 120 },
    {
      field: 'updatedAt',
      headerName: t('common.updated'),
      width: 150,
      valueFormatter: (v: string) => formatDateTime(v),
    },
  ];

  const handleCreate = async () => {
    if (!organizationId) {
      showError(t('orgContacts.needOrganization'));
      return;
    }
    try {
      const res = await createContact({
        variables: {
          input: {
            organizationId,
            firstName: firstName || undefined,
            lastName: lastName || undefined,
            email: email || undefined,
            position: position || undefined,
            salutation: salutation || undefined,
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      if (!res.data?.createOrganizationContact?.organizationContact) {
        showError(t('errors.generic'));
        return;
      }
      showSuccess(t('orgContacts.created'));
      setOpen(false);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPosition('');
      setSalutation('');
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h4">{t('orgContacts.title')}</Typography>
        <Button variant="contained" onClick={() => setOpen(true)} disabled={!organizationId}>
          {t('orgContacts.create')}
        </Button>
      </Box>

      <DataTable<Row>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={rows.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        paginationMode="client"
        onRowClick={(row) => navigate(`/organization-contacts/${row.id}`)}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('orgContacts.create')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('orgContacts.salutation')}
            value={salutation}
            onChange={(e) => setSalutation(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('orgContacts.firstName')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('orgContacts.lastName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('organizationDetail.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('orgContacts.position')}
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !organizationId}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
